import React, { useState, useEffect, useMemo } from "react";
import { useQueryProcessedDataViewsQuery, useProcessedDataTableHeadersQuery } from "graphql/types/graphql";
import { context } from "utils/Provider";
import { useHeadroomContext } from "../context/HeadRoomProvider";
import { useErrorSnackbar } from "./useErrorSnackbar";

export const useHeadroomData = () => {
  const { selectedCube } = React.useContext(context);
  const { tableName } = useHeadroomContext();
  const { showError, clearError } = useErrorSnackbar();
  const [pageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const [allFetchedViews, setAllFetchedViews] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [projectSearchTerm, setProjectSearchTerm] = useState("");

  const { data, loading, error, fetchMore, refetch } = useQueryProcessedDataViewsQuery({
    variables: {
      includeHeadroomData: true,
      cubeId: selectedCube.cubeId,
      limit: pageSize,
      offset: 0,
    },
    skip: !selectedCube.cubeId,
  });

  const {
    data: processedData,
    loading: processedDataLoading,
    error: processedDataError,
  } = useProcessedDataTableHeadersQuery({
    variables: {
      tableName: tableName || "",
    },
    skip: !tableName,
  });

  useEffect(() => {
    if (error) {
      showError(error.message || "Failed to fetch data views!");
    } else if (processedDataError) {
      showError(processedDataError.message || "Failed to fetch processed data!");
    } else {
      clearError();
    }
  }, [error, processedDataError, showError, clearError]);

  useEffect(() => {
    if (selectedCube.cubeId) {
      setCurrentPage(0);
      setAllFetchedViews([]);
      refetch({
        cubeId: selectedCube.cubeId,
        limit: pageSize,
        offset: 0,
      });
    }
  }, [selectedCube.cubeId, refetch, pageSize]);

  useEffect(() => {
    if (data?.queryProcessedDataViews?.edges) {
      const newViewNames = data.queryProcessedDataViews.edges.map((edge) => edge.name);
      setAllFetchedViews((prev) => Array.from(new Set([...prev, ...newViewNames])));
    }
  }, [data]);

  useEffect(() => {
    return () => {
      clearError();
    };
  }, [clearError]);

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return allFetchedViews;
    return allFetchedViews.filter((view) => view.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [allFetchedViews, searchTerm]);

  const handleFetchMore = () => {
    if (!loading && data?.queryProcessedDataViews?.hasNextPage) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchMore({
        variables: {
          offset: nextPage * pageSize,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;
          return {
            queryProcessedDataViews: {
              ...fetchMoreResult.queryProcessedDataViews,
              edges: [
                ...(prev.queryProcessedDataViews?.edges || []),
                ...(fetchMoreResult.queryProcessedDataViews?.edges || []),
              ],
            },
          };
        },
      }).catch((fetchError) => {
        showError(fetchError.message || "Failed to fetch more data!");
      });
    }
  };

  return {
    data,
    loading: loading || processedDataLoading,
    processedData,
    filteredOptions,
    searchTerm,
    setSearchTerm,
    projectSearchTerm,
    setProjectSearchTerm,
    handleFetchMore,
    columns: processedData?.processedDataTableHeaders?.columns || [],
  };
};
